/**
 * Persist a pending file upload across the Google OAuth redirect.
 *
 * Flow:
 *   1. User picks a photo on /upload while signed out
 *   2. User clicks the primary CTA — we call savePendingUpload(file)
 *   3. We redirect to auth.emergentagent.com → Google → back to /upload
 *   4. /upload mount calls loadPendingUpload() — if present, restores the
 *      File and auto-triggers generate
 *
 * Implementation: sessionStorage data URL. Limits per origin are ~5-10MB
 * which matches our 10MB upload cap closely enough. If the file is too
 * large to fit, we return a soft failure and tell the user to re-upload.
 */

const KEY = "facemax_pending_upload_v1";
// 4.5MB encoded → ~3.3MB raw — comfortably under sessionStorage quota.
const MAX_BYTES = 4_500_000;

export async function savePendingUpload(file) {
    if (!file) return false;
    if (file.size > MAX_BYTES) {
        return false;
    }
    try {
        const dataUrl = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result);
            r.onerror = () => reject(r.error);
            r.readAsDataURL(file);
        });
        sessionStorage.setItem(
            KEY,
            JSON.stringify({
                name: file.name,
                type: file.type,
                size: file.size,
                dataUrl,
                savedAt: Date.now(),
            }),
        );
        return true;
    } catch (_) {
        return false;
    }
}

export function hasPendingUpload() {
    try {
        return !!sessionStorage.getItem(KEY);
    } catch (_) {
        return false;
    }
}

export async function loadPendingUpload() {
    let payload;
    try {
        const raw = sessionStorage.getItem(KEY);
        if (!raw) return null;
        payload = JSON.parse(raw);
    } catch (_) {
        return null;
    }
    if (!payload?.dataUrl) return null;
    try {
        const res = await fetch(payload.dataUrl);
        const blob = await res.blob();
        return new File([blob], payload.name || "upload.jpg", {
            type: payload.type || blob.type,
            lastModified: payload.savedAt || Date.now(),
        });
    } catch (_) {
        return null;
    }
}

export function clearPendingUpload() {
    try {
        sessionStorage.removeItem(KEY);
    } catch (_) {
        /* ignore */
    }
}
